/**
 * useActiveListView — owns the single "active view" identity for a CRM list,
 * unifying built-in views, saved views and "All" under one state value, one URL
 * param, and one synchronous baseline.
 *
 * This hook is declared BEFORE useSavedViews in the list page, because
 * `builtInBaselineGroups` feeds `filtersWithSearch` (the query filter) and must
 * not depend on any useSavedViews output. useSavedViews receives `activeView`
 * from here and calls `setActiveView` / `resetToDefaultView` back to report the
 * outcome of an async saved-view apply — so this hook is the single source of
 * truth for which view is active and the single writer of the URL param.
 *
 * DOCTRINE: the URL always reflects what the UI is showing. Once the active
 * view has resolved, `?view=` is ALWAYS present — there is no clean-URL state
 * whose meaning has to be inferred:
 * - All          -> `all`             (`?view=all`)
 * - saved view   -> raw uuid          (`?view=<uuid>`)
 * - built-in     -> `builtin:<id>`    (`?view=builtin:all-types`)
 *
 * `view` carries WHICH view is selected. Display mode (table vs board) is a
 * separate, orthogonal param (`?mode=`) — the two are combinable, so they can
 * never share one param.
 *
 * Legacy links are decoded and rewritten in place (replace, so they self-heal):
 * - `?savedView=<uuid>` — the param's previous name.
 * - `?view=table|board|pipeline` — `view`'s previous meaning (display mode).
 *   These tokens are a closed set, so they can never collide with a uuid.
 *
 * The active view is seeded synchronously from the URL on mount, so the
 * param-sync effect's first write is idempotent and never clobbers a deep link.
 *
 * `options.defaultView` is a FALLBACK FOR A BARE URL ONLY. It is applied once,
 * and only when the URL carries no state-bearing param at all (no view, no
 * filters/sort/search) — any param that says something about what to show wins
 * over a stored preference. Applying a default DOES write `?view=`, exactly as
 * an explicit selection would: the resulting URL states which view is on screen
 * rather than leaving the reader to re-resolve the preference. Stale-saved-view
 * screening and filter application are the caller's responsibility.
 */

import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { FilterGroup } from '@/types/crm';
import { convertConditionGroupsToFilterGroups } from '@/utils/filterUtils';

import type { ActiveListView, BuiltInViewConfig } from '../core/types';
import { MODE_PARAM } from '../list/urlListParams';

/** Single URL param persisting WHICH view is active. */
export const ACTIVE_VIEW_PARAM = 'view';
/** Previous name of {@link ACTIVE_VIEW_PARAM}; decoded for back-compat only. */
export const LEGACY_ACTIVE_VIEW_PARAM = 'savedView';
// Prefix distinguishing a built-in view id from a saved-view uuid in the param.
const BUILTIN_PARAM_PREFIX = 'builtin:';
/** Reserved token for the All (no view filters) selection. */
export const ALL_PARAM_TOKEN = 'all';
// `?view=` previously carried the display mode. These tokens are a closed set
// and can never be a saved-view uuid, so a legacy link is unambiguous: the
// value is a MODE, and carries no view identity (the caller migrates it to
// `?mode=`). Kept in sync with resolveExplicitView in CRMObjectListPage.
const LEGACY_MODE_TOKENS: ReadonlySet<string> = new Set([
  'table',
  'board',
  'pipeline',
]);

/**
 * Params that say something about what the list should show. The stored default
 * view is a fallback for a URL carrying NONE of these — any one of them means
 * the URL is already describing a state, which always wins over a preference.
 * Unrelated params (tracking, slideout targets) deliberately do NOT count.
 *
 * The view param is the one exception to "presence alone counts": a legacy
 * display-mode token (`table`/`board`/`pipeline`) in `?view=` names no view
 * identity — `decodeActiveListViewParam` treats it as unresolved — so it must
 * NOT count as state-bearing here either, or a bookmarked/legacy `?view=table`
 * link would permanently suppress the default-view application even though the
 * URL doesn't actually name a view. `filters`/`sort`/`search` keep the raw
 * presence check; only the view param's contribution is resolved-vs-unresolved.
 */
const STATE_BEARING_PARAMS: readonly string[] = [
  ACTIVE_VIEW_PARAM,
  LEGACY_ACTIVE_VIEW_PARAM,
  'filters',
  'sort',
  'search',
];

/**
 * A pointer to the view that should be activated when no `?savedView` param is
 * present and the user has not yet made an explicit selection.
 *
 * - `kind: 'saved'`   — activate the saved view with the given `id`.
 * - `kind: 'builtin'` — activate the built-in view with the given `id`, only if
 *   it exists in `builtInViews`; otherwise the pointer is silently ignored.
 * - `kind: 'all'`     — activate explicit All on lists without built-in views;
 *   ignored on lists that declare `builtInViews` (they always have a default).
 *
 * The caller must pass a stable reference (memoized or constant) to prevent
 * spurious re-runs of the default-application effect. Stale-saved-view screening
 * and filter application are the caller's job.
 */
export type ListDefaultViewRef = {
  kind: 'saved' | 'builtin' | 'all';
  id?: string;
};

/**
 * Result of decoding the view param. `resolved: false` means the URL carries no
 * usable view identity (absent, empty, a legacy display-mode token, or a
 * `builtin:` id that no longer exists) and the caller should fall back. It is
 * deliberately distinct from `{ resolved: true, view: null }`, which is the
 * positive statement "All is selected" — conflating those two is what let a
 * param-less URL silently masquerade as a real selection.
 */
export type DecodedActiveListView =
  | { resolved: true; view: ActiveListView | null }
  | { resolved: false };

const UNRESOLVED: DecodedActiveListView = { resolved: false };

/**
 * Decode the raw URL param value into an active-view identity.
 *
 * Fails closed: an unknown `builtin:<id>` (stale deep link, renamed view) and a
 * legacy display-mode token both decode to unresolved rather than guessing, so
 * the caller applies its own fallback instead of half-applying a bad link. Any
 * other non-empty value is treated as a saved-view id.
 */
export function decodeActiveListViewParam(
  raw: string | null,
  builtInViews: BuiltInViewConfig[] | undefined
): DecodedActiveListView {
  if (!raw) {
    return UNRESOLVED;
  }
  // Legacy `?view=board` etc. carried the display mode, never an identity.
  if (LEGACY_MODE_TOKENS.has(raw)) {
    return UNRESOLVED;
  }
  if (raw === ALL_PARAM_TOKEN) {
    // On a list WITH built-in views there is no "All" — fall back to the
    // default built-in rather than leaving the query unscoped.
    return (builtInViews?.length ?? 0) > 0
      ? UNRESOLVED
      : { resolved: true, view: null };
  }
  if (raw.startsWith(BUILTIN_PARAM_PREFIX)) {
    const id = raw.slice(BUILTIN_PARAM_PREFIX.length);
    return (builtInViews ?? []).some(v => v.id === id)
      ? { resolved: true, view: { kind: 'builtin', id } }
      : UNRESOLVED;
  }
  return { resolved: true, view: { kind: 'saved', id: raw } };
}

/**
 * Encode an active-view identity into the raw URL param value. ALWAYS returns a
 * token — every state the UI can be in has a name in the URL, including All.
 */
export function encodeActiveListViewParam(view: ActiveListView | null): string {
  if (!view) {
    return ALL_PARAM_TOKEN;
  }
  if (view.kind === 'saved') {
    return view.id;
  }
  return `${BUILTIN_PARAM_PREFIX}${view.id}`;
}

/** Reference-independent equality for two active-view identities. */
function activeListViewsEqual(
  a: ActiveListView | null,
  b: ActiveListView | null
): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  return a.kind === b.kind && a.id === b.id;
}

export interface UseActiveListViewReturn {
  /** The single active view, or null for the ephemeral "All" (no built-ins). */
  activeView: ActiveListView | null;
  /**
   * Set the active view. The URL param is synced from this via an effect, so
   * this is the only way the active view (and its URL persistence) changes.
   */
  setActiveView: Dispatch<SetStateAction<ActiveListView | null>>;
  /** Baseline filter groups for the active built-in view (empty otherwise). */
  builtInBaselineGroups: FilterGroup[];
  /** Id of the default (first) built-in view, or null when none are configured. */
  defaultBuiltInViewId: string | null;
  /** Reset to the default built-in view, or "All" when none are configured. */
  resetToDefaultView: () => void;
}

export function useActiveListView(
  builtInViews: BuiltInViewConfig[] | undefined,
  options?: { defaultView?: ListDefaultViewRef | null }
): UseActiveListViewReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  const defaultBuiltInViewId = builtInViews?.[0]?.id ?? null;

  // Whether the mount-time URL described a state at all — the sole gate on
  // applying the stored default. One-shot; never mutated.
  //
  // The view param is checked via decodeActiveListViewParam rather than raw
  // presence: a legacy `?view=table|board|pipeline` token names no view
  // identity (decodes to unresolved), so it must not count as state-bearing —
  // otherwise a bookmarked/legacy link would permanently suppress the
  // default-view application even though the URL doesn't actually name a view.
  const mountHadNoStateParamsRef = useRef(
    !STATE_BEARING_PARAMS.some(p => {
      if (p === ACTIVE_VIEW_PARAM || p === LEGACY_ACTIVE_VIEW_PARAM) {
        return false;
      }
      return searchParams.has(p);
    }) &&
      !decodeActiveListViewParam(
        searchParams.get(ACTIVE_VIEW_PARAM) ??
          searchParams.get(LEGACY_ACTIVE_VIEW_PARAM),
        builtInViews
      ).resolved
  );
  // True until the user makes an explicit view selection (gates default application).
  const pristineRef = useRef(true);
  // Guards the one-shot default application — once true, the effect is a no-op.
  const defaultAppliedRef = useRef(false);

  const [activeView, setActiveView] = useState<ActiveListView | null>(() => {
    const decoded = decodeActiveListViewParam(
      searchParams.get(ACTIVE_VIEW_PARAM) ??
        searchParams.get(LEGACY_ACTIVE_VIEW_PARAM),
      builtInViews
    );
    if (decoded.resolved) {
      return decoded.view;
    }
    return defaultBuiltInViewId
      ? { kind: 'builtin', id: defaultBuiltInViewId }
      : null;
  });

  // Resolve the active built-in view's config (null when a saved view or "All"
  // is active). Drives the synchronous baseline below.
  const activeBuiltInView = useMemo(() => {
    if (activeView?.kind !== 'builtin') {
      return null;
    }
    return (builtInViews ?? []).find(v => v.id === activeView.id) ?? null;
  }, [builtInViews, activeView]);

  const builtInBaselineGroups = useMemo<FilterGroup[]>(
    () =>
      activeBuiltInView
        ? convertConditionGroupsToFilterGroups([
            { id: 'builtin', conditions: activeBuiltInView.baselineConditions },
          ]).filter_groups
        : [],
    [activeBuiltInView]
  );

  // Held in a ref so the state->URL effect can call the router setter without
  // listing it as a dependency. react-router hands back a fresh setSearchParams
  // identity after each navigation; depending on it would re-run the effect on
  // every URL write and spam redundant replace-navigations.
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  // Exposed setter that records an explicit user selection, permanently
  // retiring the one-shot default for this mount. Internal effects (URL->state,
  // default application) use the raw `setActiveView` so they don't self-disable.
  //
  // No "is this All explicit?" bookkeeping is needed any more: All has its own
  // token and is written like any other view, so an explicitly-chosen All and a
  // defaulted All are the same URL and cannot be confused.
  const userSetActiveView = useCallback(
    (valueOrUpdater: SetStateAction<ActiveListView | null>) => {
      pristineRef.current = false;
      setActiveView(valueOrUpdater);
    },
    []
  );

  // State -> URL: mirror the active view into the view param. State is the
  // source of truth (seeded from the URL on mount). Every state the UI can be in
  // has a token, so this ALWAYS writes a param — including All, and including a
  // view that was applied from the stored default. Runs on activeView changes
  // only — NOT on searchParams changes — so an external URL change can never be
  // immediately overwritten by a stale activeView here.
  //
  // The same write retires legacy params so old links self-heal:
  // - `?savedView=` is dropped (its value already seeded `activeView`).
  // - `?view=<mode>` had `view` meaning display mode; its token is moved to
  //   `?mode=` in this same navigation, so the mode survives being overwritten
  //   here. An existing `?mode=` always wins (it is the current vocabulary).
  // Whether the URL currently names a view at all. A bare URL, a legacy mode
  // token, or a stale `builtin:<id>` all count as "names no view" — and each
  // must be re-asserted even when activeView itself has not changed, since the
  // effect below is otherwise driven only by state transitions. Note this flips
  // only when the param's RESOLVABILITY changes, so routine writes by other
  // param owners (`?filters=`, `?sort=`) never re-trigger it.
  const urlNamesNoView = !decodeActiveListViewParam(
    searchParams.get(ACTIVE_VIEW_PARAM) ??
      searchParams.get(LEGACY_ACTIVE_VIEW_PARAM),
    builtInViews
  ).resolved;

  // The activeView this effect last observed, so it can tell a state-driven run
  // (a real selection — always wins) from a URL-driven one (defer to the
  // URL->state effect). `activeView` alone cannot distinguish them.
  const lastSyncedViewRef = useRef(activeView);

  useEffect(() => {
    const viewChanged = !activeListViewsEqual(
      lastSyncedViewRef.current,
      activeView
    );
    lastSyncedViewRef.current = activeView;

    const desired = encodeActiveListViewParam(activeView);
    const current = searchParams.get(ACTIVE_VIEW_PARAM);
    const decodedCurrent = decodeActiveListViewParam(current, builtInViews);
    // This run was triggered by the URL alone (activeView is unchanged) and the
    // URL names a DIFFERENT valid view — an external navigation is in flight
    // and the URL->state effect owns reconciling it. Writing here would clobber
    // that navigation with a now-stale activeView. When activeView DID change
    // it is a real selection and must always win, even over a resolvable param.
    if (
      !viewChanged &&
      decodedCurrent.resolved &&
      !activeListViewsEqual(decodedCurrent.view, activeView)
    ) {
      return;
    }
    const legacyModeToken =
      current !== null && LEGACY_MODE_TOKENS.has(current) ? current : null;
    const hasLegacyViewParam = searchParams.has(LEGACY_ACTIVE_VIEW_PARAM);
    if (
      desired === current &&
      !hasLegacyViewParam &&
      legacyModeToken === null
    ) {
      return;
    }
    setSearchParamsRef.current(
      (prev: URLSearchParams) => {
        const next = new URLSearchParams(prev);
        if (legacyModeToken !== null && !next.has(MODE_PARAM)) {
          next.set(MODE_PARAM, legacyModeToken);
        }
        next.set(ACTIVE_VIEW_PARAM, desired);
        next.delete(LEGACY_ACTIVE_VIEW_PARAM);
        return next;
      },
      { replace: true }
    );
    // searchParams is read only to decide whether to write; it is intentionally
    // NOT a dependency (see the effect comment above) — depending on it would
    // run this effect on every unrelated param write with a possibly-stale
    // activeView. `urlNamesNoView` is the one narrow slice of it we DO depend
    // on, and the guard above makes acting on it safe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, urlNamesNoView]);

  // URL -> state: reflect external URL changes (browser back/forward, a
  // deep-link param edit) back into the active view. Guarded by an equality
  // check so it only updates when the decoded view genuinely differs, which
  // prevents an update loop with the state->URL effect and lets React bail out
  // of the no-op setState.
  //
  // Only an UNRESOLVED param falls back to the default view. A param that
  // resolves to All is honoured as All — the distinction the DecodedActiveListView
  // discriminator exists to preserve. (Previously an absent param and an explicit
  // All were indistinguishable here, so any unrelated URL write — e.g. the filter
  // sync hook adding `?filters=` — silently reset the active view to the fallback
  // and orphaned the user's edits.)
  useEffect(() => {
    const decoded = decodeActiveListViewParam(
      searchParams.get(ACTIVE_VIEW_PARAM) ??
        searchParams.get(LEGACY_ACTIVE_VIEW_PARAM),
      builtInViews
    );
    // PRECEDENCE: a param that NAMES a view is an instruction and wins over
    // current state. A param that names none is an ABSENCE, not an instruction
    // to reset — state stands, and the state->URL effect re-states it.
    //
    // Treating absence as "reset to the fallback" is what destroyed the active
    // view mid-session: any unrelated param write produced a URL with no view
    // in it, which this effect then read as "no view is selected". It is also
    // what would make the two effects oscillate now that the counterpart always
    // writes a token — one clearing the view, the other re-asserting it.
    if (!decoded.resolved) {
      return;
    }
    const next = decoded.view;
    setActiveView((prev: ActiveListView | null) =>
      activeListViewsEqual(prev, next) ? prev : next
    );
  }, [searchParams, builtInViews, defaultBuiltInViewId]);

  // One-shot default application. Conditions that must ALL hold:
  //   (a) the mount-time URL carried NO state-bearing param — not just no view
  //       param. A link that says `?filters=…` is already describing what to
  //       show, and a stored preference must not override it.
  //   (b) options.defaultView is non-null (feature enabled)
  //   (c) user has not yet made an explicit selection (pristine)
  // Mapping:
  //   saved   -> activate the saved view id
  //   builtin -> activate if the id exists in builtInViews, else ignore
  //   all     -> All, only on lists without builtInViews (they have no All)
  // Applying a default DOES write `?view=` — the state->URL effect treats it
  // exactly like an explicit selection, so the URL always names what is shown.
  const defaultView = options?.defaultView;
  useEffect(() => {
    if (!defaultView) {
      return;
    }
    if (!mountHadNoStateParamsRef.current) {
      return;
    }
    if (!pristineRef.current) {
      return;
    }
    if (defaultAppliedRef.current) {
      return;
    }

    const hasBuiltIns = (builtInViews?.length ?? 0) > 0;

    if (defaultView.kind === 'saved' && defaultView.id) {
      setActiveView({ kind: 'saved', id: defaultView.id });
      defaultAppliedRef.current = true;
    } else if (defaultView.kind === 'builtin' && defaultView.id) {
      const exists = (builtInViews ?? []).some(v => v.id === defaultView.id);
      if (exists) {
        setActiveView({ kind: 'builtin', id: defaultView.id });
        defaultAppliedRef.current = true;
      }
    } else if (defaultView.kind === 'all' && !hasBuiltIns) {
      setActiveView(null);
      defaultAppliedRef.current = true;
    }
  }, [defaultView, builtInViews]);

  // Select All (or, on a list with built-in views, the default built-in). This
  // is a USER action — it routes through userSetActiveView so it counts as an
  // explicit selection and writes `?view=all`. Using the raw setter here was
  // what made All unreachable once a default view was set: the click changed
  // state but wrote no param, so the next mount re-applied the default.
  const resetToDefaultView = useCallback(() => {
    userSetActiveView(
      defaultBuiltInViewId
        ? { kind: 'builtin', id: defaultBuiltInViewId }
        : null
    );
  }, [defaultBuiltInViewId, userSetActiveView]);

  return {
    activeView,
    setActiveView: userSetActiveView,
    builtInBaselineGroups,
    defaultBuiltInViewId,
    resetToDefaultView,
  };
}
